import React, { useCallback, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import WorkTemplateCreationModal from '@atom/components/common/workTemplateCreationModal/WorkTemplateCreationModal';
import { CalendarGap } from '@atom/components/workOrders/workOrdersCalendar/weekViewUtils';
import textConstants from '@atom/constants/textConstants';
import { GET_TASK_TEMPLATES } from '@atom/graph/taskTemplate';
import {
  WORK_ORDER_BULK_CREATE,
  WORK_ORDER_FULL_CREATE,
} from '@atom/graph/work';
import { GET_WORK_ORDER_TEMPLATE } from '@atom/graph/workTemplate';
import { useCurrentLocation } from '@atom/hooks/useCurrentLocation';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Button, Icon, Modal, Snackbar } from '@atom/mui';
import { ChevronLeftIcon, ChevronRightIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetDetail, SearchAsset } from '@atom/types/asset';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { WorkOrderDetailType, WorkOrderType } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { templateTaskToTask } from '@atom/utilities/taskUtilities';
import { generateYYYYMMName } from '@atom/utilities/timeUtilities';

import BulkWorkDetailsStep from './steps/BulkWorkDetailsStep';
import NoTaskGuardModal from './steps/tasks/NoTaskGuardModal';
import TasksStep from './steps/TasksStep';
import WorkDetailsStep from './steps/WorkDetailsStep';
import WorkTemplateStep from './steps/WorkTemplateStep';
import BulkCreateConfirmModal from './BulkCreateConfirmModal';
import CreateWorkModalContext, {
  initialDraft,
  ModalStep,
  WorkCreateType,
  WorkOrderDraft,
  WorkTemplateSelection,
} from './CreateWorkModalContext';
import {
  addBlankTask,
  applyCascadeDefaultValues,
  getModalStepsArray,
  getNoTaskGuardCreateView,
  getRequiredOnCreateInvalidFieldIds,
  isStepDisabled,
  workOrderDraftToBulkCreateInput,
  workOrderDraftToFullCreateInput,
} from './createWorkModalUtilities';
import StepperSidebar from './StepperSidebar';

import classes from './createWorkModal.module.css';

const styles = {
  modalContent: {
    padding: 0,
  },
};

type CreateWorkModalProps = {
  open: boolean;
  onClose: () => void;
  type?: WorkCreateType;
  workTemplateRequired?: boolean;
  onWorkOrderCreate?: (workOrder: WorkOrderDetailType) => void;
  redirectOnCreate?: boolean;
  fromCalendarDate?: Date;
  fromCalendarGap?: CalendarGap;
  asset?: InventoryAssetDetailType | SearchAsset | AssetDetail;
  'data-cy'?: string;
};

const CreateWorkModal = (props: CreateWorkModalProps) => {
  const {
    open,
    onClose,
    type,
    workTemplateRequired,
    onWorkOrderCreate,
    redirectOnCreate,
    fromCalendarDate,
    fromCalendarGap,
    asset,
    'data-cy': dataCy = 'createWorkModal',
  } = props;

  const [, { navigate }] = useNavigation();
  const { pointLocation } = useCurrentLocation();

  const assetLocked = !!asset;

  const getInitialDraft = (): WorkOrderDraft => ({
    ...initialDraft,
    mode: type ?? 'SINGLE',
    fromCalendarDate,
    fromCalendarGap,
    ...(asset
      ? {
          asset,
          assetId: asset.id,
          isLocationBased: !R.isNil(asset.locationType),
        }
      : {}),
  });

  const [activeStep, setActiveStep] = useState<ModalStep>(ModalStep.TEMPLATE);
  const [templateModalOpen, setTemplateModalOpen] = useState<boolean>(false);
  const [createDisabled, setCreateDisabled] = useState<boolean>(false);
  const [guardOpen, setGuardOpen] = useState<boolean>(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState<boolean>(false);
  const [taskTemplatePickerOpen, setTaskTemplatePickerOpen] =
    useState<boolean>(false);
  const [draft, setDraft] = useState<WorkOrderDraft>(getInitialDraft);
  const [requiredFieldIds, setRequiredFieldIds] = useState<Set<string>>(
    new Set([]),
  );

  const showModeToggle =
    hasRolePermissions(ROLE_SETS.MANAGER) && !type && !assetLocked;

  const preferences = usePreferences();
  const requireTaskType = R.pathOr(
    false,
    ['workOrders', 'requireTaskType'],
    preferences,
  );
  const disableTaskCreation = R.pathOr(
    false,
    ['workOrders', 'disableTaskCreation'],
    preferences,
  );

  const { data: taskTemplatesData, loading: taskTemplatesLoading } = useQuery(
    GET_TASK_TEMPLATES,
    {
      variables: {
        input: {
          sortBy: 'name',
          isAscending: true,
          limit: 1,
          page: 1,
          workTemplateId: draft.workTemplate?.id || undefined,
          schemaId: draft.asset?.schemaId || undefined,
        },
      },
      fetchPolicy: 'network-only',
      skip: activeStep !== ModalStep.TASKS,
    },
  );

  const hasTaskTemplates =
    taskTemplatesLoading ||
    !taskTemplatesData ||
    (taskTemplatesData.taskTemplates?.totalCount || 0) > 0;

  const [createWork, { loading }] = useMutation(WORK_ORDER_FULL_CREATE);

  const [bulkCreateWork, { loading: bulkLoading }] = useMutation(
    WORK_ORDER_BULK_CREATE,
  );

  const [getWorkTemplate, { loading: templateLoading }] = useLazyQuery(
    GET_WORK_ORDER_TEMPLATE,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const updateDraftField = useCallback(
    <K extends keyof WorkOrderDraft>(key: K, value: WorkOrderDraft[K]) =>
      setDraft(prev => ({ ...prev, [key]: value })),
    [],
  );

  const STEPS_ARRAY = getModalStepsArray(draft.mode, disableTaskCreation);

  const resetState = () => {
    setActiveStep(ModalStep.TEMPLATE);
    setCreateDisabled(false);
    setGuardOpen(false);
    setBulkConfirmOpen(false);
    setTaskTemplatePickerOpen(false);
    setDraft(getInitialDraft());
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleCreateWorkTemplate = () => {
    setTemplateModalOpen(true);
  };

  const handleTemplateClick = async (template: WorkTemplateSelection) => {
    const workTemplateType = R.pathOr<WorkOrderType>(
      WorkOrderType.DEFAULT,
      ['type'],
      template,
    );

    setDraft(prev => ({
      ...prev,
      workTemplate: template,
      workTemplateType,
      name: generateYYYYMMName(template),
      ...(assetLocked
        ? {}
        : { asset: null, assetId: null, isLocationBased: false }),
      startedFromScratch: false,
    }));

    const { data } = await getWorkTemplate({
      variables: { id: template.id },
    });

    const fullTemplate = data?.workOrderTemplate;

    if (fullTemplate) {
      setDraft(prev => ({
        ...prev,
        fields: ((fullTemplate.fields as any) || []).map(
          applyCascadeDefaultValues,
        ),
        fieldOrder: fullTemplate.fieldOrder || [],
        summary: fullTemplate.summary || [],
        tasks: templateTaskToTask(fullTemplate.tasks || []),
      }));
    }

    setActiveStep(ModalStep.DETAILS);
  };

  // Flags required-on-create fields missing a value when leaving the details
  // step. Returns true when the step has invalid fields and should not advance.
  // Bulk details does not collect custom fields, so it is never checked.
  const hasInvalidRequiredFields = (): boolean => {
    if (activeStep !== ModalStep.DETAILS || draft.mode === 'BULK') {
      return false;
    }

    const requiredFields = getRequiredOnCreateInvalidFieldIds(draft.fields);
    setRequiredFieldIds(new Set(requiredFields));

    return requiredFields.length > 0;
  };

  const handleStep = (value: number) => {
    if (value > 0 && hasInvalidRequiredFields()) {
      return;
    }

    const currentStepIndex = R.findIndex(R.propEq(activeStep, 'value'))(
      STEPS_ARRAY,
    );
    const newStep = STEPS_ARRAY[currentStepIndex + value];

    if (newStep) {
      setActiveStep(newStep.value);
    }
  };

  const submitWork = async () => {
    if (
      draft.isLocationBased &&
      !draft.assetId &&
      !draft.asset &&
      !pointLocation
    ) {
      Snackbar.error({
        message: 'Location is not available yet. Please try again in a moment.',
      });
      return;
    }

    try {
      const input = workOrderDraftToFullCreateInput(draft, pointLocation);

      const res = await createWork({ variables: { input } });
      const workOrderData = res?.data?.workOrderFullCreate;

      if (!workOrderData) {
        throw new Error('Work order creation returned no data.');
      }

      if (redirectOnCreate) {
        navigate(`/workOrders/${workOrderData.id}`);
      } else {
        Snackbar.info({
          message: `Created work ${workOrderData.name}.`,
          action: 'View',
          onActionClick: () => navigate(`/workOrders/${workOrderData.id}`),
        });
      }

      if (onWorkOrderCreate) {
        onWorkOrderCreate(workOrderData);
      }

      handleClose();
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const submitBulkWork = async () => {
    if (draft.isLocationBased && !pointLocation) {
      Snackbar.error({
        message: 'Location is not available yet. Please try again in a moment.',
      });
      return;
    }

    try {
      const input = workOrderDraftToBulkCreateInput(draft, pointLocation);

      const res = await bulkCreateWork({ variables: { input } });
      const totalWorkOrders = res?.data?.workOrderBulkCreate?.totalWorkOrders;

      if (R.isNil(totalWorkOrders)) {
        throw new Error('Bulk work order creation returned no data.');
      }

      Snackbar.info({
        message: `Created ${totalWorkOrders} work ${
          totalWorkOrders > 1 ? 'orders' : 'order'
        }.`,
        action: 'View',
        onActionClick: () => navigate('/workOrders'),
      });

      handleClose();
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const handleCreate = () => {
    // Details is the last step when task creation is disabled, so required
    // fields must be checked here as well
    if (hasInvalidRequiredFields()) {
      return;
    }

    if (draft.mode === 'BULK') {
      if (draft.quantity > 1) {
        setBulkConfirmOpen(true);
        return;
      }

      submitBulkWork();
      return;
    }

    if ((draft.tasks || []).length === 0 && !disableTaskCreation) {
      setGuardOpen(true);
      return;
    }

    submitWork();
  };

  const handleBulkConfirm = () => {
    setBulkConfirmOpen(false);
    submitBulkWork();
  };

  const handleGuardCreateWorkAnyway = () => {
    setGuardOpen(false);
    submitWork();
  };

  const handleGuardCreateTask = () => {
    setGuardOpen(false);

    const view = getNoTaskGuardCreateView(requireTaskType, hasTaskTemplates);

    if (view === 'template') {
      setTaskTemplatePickerOpen(true);
    } else {
      updateDraftField('tasks', addBlankTask(draft.tasks || []));
    }
  };

  const getContent = (): React.ReactNode => {
    const content = {
      [ModalStep.TEMPLATE]: <WorkTemplateStep />,
      [ModalStep.DETAILS]:
        draft.mode === 'BULK' ? <BulkWorkDetailsStep /> : <WorkDetailsStep />,
      [ModalStep.TASKS]: <TasksStep />,
    };

    return content[activeStep];
  };

  const getFooter = (): React.ReactNode => {
    const currentStepIndex = R.findIndex(R.propEq(activeStep, 'value'))(
      STEPS_ARRAY,
    );
    const isLastStep = currentStepIndex === STEPS_ARRAY.length - 1;
    const showBackButton = activeStep !== ModalStep.TEMPLATE;

    const isDisabled =
      isStepDisabled(activeStep, draft, createDisabled) ||
      templateLoading ||
      loading ||
      bulkLoading ||
      requiredFieldIds.size > 0;
    const rightArrowColor = isDisabled
      ? colors.neutral.gray
      : colors.neutral.white;

    return (
      <div className={classes.footer}>
        {showBackButton ? (
          <Button
            onClick={() => handleStep(-1)}
            data-cy="createWorkModalBackButton"
          >
            <Icon icon={ChevronLeftIcon} />
            Back
          </Button>
        ) : (
          <div />
        )}
        <div>
          <Button
            onClick={handleClose}
            style={{ marginRight: '0.5rem' }}
            data-cy="createWorkModalCancelButton"
          >
            Cancel
          </Button>
          {isLastStep ? (
            <Button
              color="primary"
              variant="contained"
              onClick={handleCreate}
              disabled={isDisabled}
              data-cy="createWorkModalCreateButton"
            >
              Create
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleStep(1)}
              disabled={isDisabled}
              data-cy="createWorkModalNextButton"
            >
              Next
              <Icon icon={ChevronRightIcon} color={rightArrowColor} />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <CreateWorkModalContext.Provider
      value={{
        activeStep,
        setActiveStep,
        STEPS_ARRAY,
        draft,
        setDraft,
        updateDraftField,
        workTemplateRequired: workTemplateRequired ?? false,
        showModeToggle,
        assetLocked,
        onCreateWorkTemplate: handleCreateWorkTemplate,
        onTemplateClick: handleTemplateClick,
        createDisabled,
        setCreateDisabled,
        taskTemplatePickerOpen,
        setTaskTemplatePickerOpen,
        requiredFieldIds,
        setRequiredFieldIds,
      }}
    >
      <Modal
        title="Create Work"
        open={open && !templateModalOpen}
        footer={getFooter()}
        contentStyle={styles.modalContent}
        width="50rem"
        loading={loading || bulkLoading}
        data-cy={dataCy}
        disableCloseOnOutsideClick
      >
        <div className={classes.modalContent}>
          <StepperSidebar />
          <div
            className={clsx(
              classes.stepContent,
              (activeStep === ModalStep.DETAILS ||
                activeStep === ModalStep.TASKS) &&
                classes.stepContentFlush,
            )}
          >
            {getContent()}
          </div>
        </div>
      </Modal>
      <WorkTemplateCreationModal
        open={templateModalOpen}
        closeModal={() => setTemplateModalOpen(false)}
      />
      <NoTaskGuardModal
        open={guardOpen}
        loading={loading}
        onCancel={() => setGuardOpen(false)}
        onCreateWorkAnyway={handleGuardCreateWorkAnyway}
        onCreateTask={handleGuardCreateTask}
      />
      <BulkCreateConfirmModal
        open={bulkConfirmOpen}
        loading={bulkLoading}
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkConfirm}
      />
    </CreateWorkModalContext.Provider>
  );
};

export default CreateWorkModal;
